import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, KeyRound, CheckCircle2, Mail } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [sentMsg, setSentMsg] = useState('');
  const [done, setDone] = useState(false);

  const requestLink = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try { const { data } = await api.post('/auth/forgot-password', { email }); setSentMsg(`${data.message} ${data.delivery_note || ''}`.trim()); setSent(true); }
    catch (err) { setError(err.response?.data?.detail || 'No se pudo procesar la solicitud.'); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault(); setError('');
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    try { await api.post('/auth/reset-password', { token, new_password: password }); setDone(true); }
    catch (err) { setError(err.response?.data?.detail || 'No se pudo restablecer la contraseña.'); }
    finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-4 py-10" data-testid="reset-page">
      <div className="w-full max-w-md space-y-6">
        <Link to="/login" data-testid="reset-back-login" className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Volver a iniciar sesión</Link>
        <div className="bg-card border border-border rounded-xl p-6 sm:p-8 card-shadow space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-brand-navy flex items-center justify-center"><KeyRound className="h-6 w-6 text-brand-gold" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Cuenta local</p>
              <h1 className="font-body font-bold text-2xl" data-testid="reset-title">{token ? 'Restablecer contraseña' : 'Recuperar acceso'}</h1>
            </div>
          </div>
          {error && <Alert variant="destructive" data-testid="reset-error"><AlertDescription>{error}</AlertDescription></Alert>}

          {token ? (
            done ? (
              <div className="space-y-4" data-testid="reset-success">
                <Alert><CheckCircle2 className="h-4 w-4" /><AlertDescription>Contraseña restablecida. Ya puedes iniciar sesión con tu nueva contraseña.</AlertDescription></Alert>
                <Button className="w-full h-11 bg-brand-navy text-brand-cream hover:bg-brand-navy-dark" data-testid="reset-goto-login" onClick={() => navigate('/login')}>Ir a iniciar sesión</Button>
              </div>
            ) : (
              <form onSubmit={doReset} className="space-y-4" data-testid="reset-form">
                <div><Label htmlFor="reset-password">Nueva contraseña (mínimo 8 caracteres)</Label><Input id="reset-password" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="mt-1 h-11" /></div>
                <div><Label htmlFor="reset-confirm">Confirmar contraseña</Label><Input id="reset-confirm" type="password" autoComplete="new-password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="mt-1 h-11" /></div>
                <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-11 bg-brand-orange text-brand-navy hover:bg-brand-orange-dark">{loading ? <Loader2 className="animate-spin" /> : 'Restablecer contraseña'}</Button>
              </form>
            )
          ) : (
            sent ? (
              <Alert data-testid="forgot-sent"><Mail className="h-4 w-4" /><AlertDescription>Si existe una cuenta local con ese correo, se generó un enlace de recuperación de un solo uso. La entrega automática por correo aún no está configurada; un administrador puede generarte y entregarte el enlace de forma segura.</AlertDescription></Alert>
            ) : (
              <form onSubmit={requestLink} className="space-y-4" data-testid="forgot-form">
                <p className="text-sm text-muted-foreground">Introduce el correo de tu cuenta de cliente. Las cuentas que usan Google gestionan la contraseña desde su proveedor.</p>
                <div><Label htmlFor="forgot-email">Correo</Label><Input id="forgot-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="mt-1 h-11" /></div>
                <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full h-11 bg-brand-navy text-brand-cream hover:bg-brand-navy-dark">{loading ? <Loader2 className="animate-spin" /> : 'Solicitar enlace de recuperación'}</Button>
              </form>
            )
          )}
        </div>
      </div>
    </main>
  );
}
